import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../../lib/api";
import { cn } from "../../lib/cn";

type AppointmentDetail = {
  id: string;
  branch_name?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_risk_tier?: string | null;
  service_name?: string | null;
  staff_name?: string | null;
  start_at: string;
  end_at: string;
  status: string;
  source_channel: string;
  notes?: string | null;
  allowed_actions: string[];
  recent_events: Array<{
    id: string;
    event_type: string;
    created_at: string;
    payload: Record<string, unknown>;
  }>;
};

const STATUS_COLORS: Record<string, string> = {
  pending_confirmation: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-gray-100 text-gray-500",
  no_show: "bg-red-100 text-red-700",
};

const STATUS_LABELS: Record<string, string> = {
  pending_confirmation: "Pending confirmation",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};

const ACTION_LABELS: Record<string, string> = {
  confirm: "Confirm ✅",
  cancel: "Cancel ❌",
  complete: "Complete ✔",
  no_show: "No-show 👻",
  reschedule: "Reschedule 🔁",
};

function formatDt(iso: string) {
  return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

export function TgAppointmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery<AppointmentDetail>({
    queryKey: ["tg-appointment", id],
    queryFn: () => apiGet(`/v3/tg/appointments/${id}`),
    enabled: !!id,
  });

  const transition = useMutation({
    mutationFn: (action: string) => apiPost(`/v3/tg/appointments/${id}/transition`, { action }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tg-appointment", id] });
      qc.invalidateQueries({ queryKey: ["tg-today"] });
    },
  });

  if (isLoading) return <div className="flex items-center justify-center h-32 text-gray-400">Loading…</div>;
  if (error || !data) return <div className="p-4 text-red-600">Appointment not found.</div>;

  const riskColors: Record<string, string> = {
    minimal: "text-green-600",
    low: "text-green-600",
    medium: "text-yellow-600",
    high: "text-red-600",
    critical: "text-red-700 font-bold",
  };

  return (
    <div className="p-4 space-y-4">
      <button onClick={() => navigate(-1)} className="text-teal-600 text-sm">← Back</button>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
        <div className="flex items-start justify-between">
          <h1 className="text-lg font-bold text-gray-900">{data.customer_name ?? "Unknown client"}</h1>
          <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", STATUS_COLORS[data.status] ?? "bg-gray-100 text-gray-600")}>
            {STATUS_LABELS[data.status] ?? data.status}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <span className="text-gray-500">Service</span>
          <span className="text-gray-900">{data.service_name}</span>
          <span className="text-gray-500">Staff</span>
          <span className="text-gray-900">{data.staff_name}</span>
          <span className="text-gray-500">Branch</span>
          <span className="text-gray-900">{data.branch_name}</span>
          <span className="text-gray-500">Start</span>
          <span className="text-gray-900">{formatDt(data.start_at)}</span>
          <span className="text-gray-500">End</span>
          <span className="text-gray-900">{formatDt(data.end_at)}</span>
          {data.customer_phone && (
            <>
              <span className="text-gray-500">Phone</span>
              <a href={`tel:${data.customer_phone}`} className="text-teal-600 underline">{data.customer_phone}</a>
            </>
          )}
          {data.customer_risk_tier && (
            <>
              <span className="text-gray-500">Risk</span>
              <span className={riskColors[data.customer_risk_tier] ?? "text-gray-600"}>{data.customer_risk_tier}</span>
            </>
          )}
        </div>

        {data.notes && (
          <div className="text-sm text-gray-600 border-t pt-2">
            <span className="font-medium text-gray-500">Notes: </span>{data.notes}
          </div>
        )}
      </div>

      {/* Actions */}
      {data.allowed_actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {data.allowed_actions.map((action) =>
            action === "reschedule" ? (
              <button
                key={action}
                onClick={() => navigate(`/tg/reschedule/${id}`)}
                className="text-sm px-4 py-2 border border-teal-600 text-teal-600 rounded-xl"
              >
                {ACTION_LABELS[action]}
              </button>
            ) : (
              <button
                key={action}
                onClick={() => transition.mutate(action)}
                disabled={transition.isPending}
                className={cn(
                  "text-sm px-4 py-2 rounded-xl text-white disabled:opacity-50",
                  action === "confirm" || action === "complete" ? "bg-teal-600" : "bg-red-500"
                )}
              >
                {transition.isPending ? "…" : ACTION_LABELS[action] ?? action}
              </button>
            )
          )}
        </div>
      )}

      {/* Timeline */}
      {data.recent_events.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Recent events</h2>
          {data.recent_events.map((ev) => (
            <div key={ev.id} className="text-sm bg-white rounded-lg border border-gray-100 px-3 py-2 flex items-center justify-between">
              <span className="font-medium text-gray-700 capitalize">{ev.event_type.replace("_", " ")}</span>
              <span className="text-xs text-gray-400">{formatDt(ev.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
